// Single place the app talks to analytics.
//
// Google Analytics 4 is loaded by the Google Tag Manager container
// (GTM-WBDFZNGB, declared in public/index.html), so the app never calls
// gtag() directly. Instead it pushes plain event objects onto the GTM data
// layer; GA4 Event tags configured in GTM turn them into GA4 events.
// The full list of events and parameters lives in docs/gtm-events.md.
//
// SSR-safe: the prerender step runs this code in Node where `window` is
// undefined, so every call is a silent no-op there.

import { preloadEstimator } from "@/lib/estimator-gate";

/**
 * Push a custom event onto the GTM data layer.
 *
 * @param {string} name   GTM/GA4 event name (snake_case, e.g. "generate_lead").
 * @param {Object} [params]  Flat event parameters; each becomes a Data Layer
 *   Variable in GTM (e.g. `service`, `urgency`).
 */
export const trackEvent = (name, params = {}) => {
  if (typeof window === "undefined" || !name) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...params });
};

// ---------------------------------------------------------------------------
// SEO audit batch 3, Part 5 — click events with a `location` key.
//
// Every tel: / sms: / mailto: link, every "Get an Instant Estimate" button
// (`data-cta="estimate"`) and every Acorn Finance link is tracked from ONE
// document-level listener instead of per-component handlers, so each tap
// pushes exactly one event no matter how often a component re-renders, and a
// link added anywhere on the site is covered automatically.
// ---------------------------------------------------------------------------

/** Allowed `location` values, in the order they are tested. */
const LOCATION_RULES = [
  ["[data-track-location]", (el) => el.getAttribute("data-track-location")],
  ['[data-testid="mobile-bottom-bar"]', () => "sticky_bar"],
  ['[data-testid="emergency-topbar"]', () => "top_bar"],
  ["header", () => "header"],
  ["footer", () => "footer"],
  // Every page hero section carries a data-testid that ends in "hero" (home-hero, service-detail-hero, ...).
  ['[data-testid$="hero"]', () => "hero"],
];

/**
 * Where on the page an element sits: "header" | "top_bar" | "sticky_bar" |
 * "hero" | "footer" | "page". A `data-track-location` attribute on any
 * ancestor overrides the DOM-based guess (used for portal content such as the
 * mobile menu sheet, which renders outside <header>).
 */
export const locationOf = (el) => {
  if (!el || typeof el.closest !== "function") return "page";
  for (const [selector, value] of LOCATION_RULES) {
    const hit = el.closest(selector);
    if (hit) return value(hit) || "page";
  }
  return "page";
};

const CTA_SELECTOR = '[data-cta="estimate"]';

const classify = (anchor) => {
  const href = (anchor.getAttribute("href") || "").trim().toLowerCase();
  if (href.startsWith("tel:")) return "phone_click";
  if (href.startsWith("sms:")) return "text_click";
  if (href.startsWith("mailto:")) return "email_click";
  return null;
};

const onDocumentClick = (event) => {
  const target = event.target;
  if (!target || typeof target.closest !== "function") return;
  // "Get an Instant Estimate" buttons (and the sticky bar's Estimate button).
  const cta = target.closest(CTA_SELECTOR);
  if (cta) {
    trackEvent("cta_estimate_click", { location: locationOf(cta) });
    // Part 3: start fetching the estimator chunk right away so /estimate renders it sooner.
    preloadEstimator();
    return;
  }
  const anchor = target.closest("a[href]");
  if (!anchor) return;
  const name = classify(anchor);
  if (name) trackEvent(name, { location: locationOf(anchor) });
};

let installed = false;

/**
 * Attach the delegated click tracker once per page load (App.js). Capture
 * phase, so a component handler that stops propagation cannot hide a tap.
 * Returns the uninstaller.
 */
export const installClickTracking = () => {
  if (typeof document === "undefined" || installed) return () => {};
  installed = true;
  document.addEventListener("click", onDocumentClick, true);
  return () => {
    installed = false;
    document.removeEventListener("click", onDocumentClick, true);
  };
};
